/**
 * Quando avisar sobre o armazenamento, e com que frase, sem tela.
 *
 * A faixa só aparece a partir de 90%: abaixo disso ela seria ruído numa tela
 * que a pessoa abre para convidar alguém. No limite, a frase muda, porque o
 * envio de arquivos já parou. A porcentagem arredonda para baixo, como no
 * medidor: 99,9% não pode dizer "100%".
 */

import { ErroDaApi } from '@cliqcrm/contracts';
import { comUmaCasa } from '@/shared/lib/formato';
import type { Dinheiro, RecusaDoLimite } from '../model/limites-da-organizacao';

/** A partir de que fração do contratado a faixa aparece. */
export const AVISO_DO_ARMAZENAMENTO = 0.9;

export type AvisoDoArmazenamento = {
  readonly noLimite: boolean;
  /** A frase principal da faixa. */
  readonly titulo: string;
};

/** O aviso da faixa, ou nulo quando ainda não é hora de avisar. */
export function avisoDoArmazenamento(
  emUsoGb: number | null,
  contratadoGb: number,
): AvisoDoArmazenamento | null {
  if (emUsoGb === null || contratadoGb <= 0) return null;
  const proporcao = emUsoGb / contratadoGb;
  if (proporcao < AVISO_DO_ARMAZENAMENTO) return null;
  const uso = `${comUmaCasa(emUsoGb)} de ${comUmaCasa(contratadoGb)} GB`;
  if (proporcao >= 1) {
    return {
      noLimite: true,
      titulo: `A organização chegou ao limite do armazenamento (${uso}).`,
    };
  }
  return {
    noLimite: false,
    titulo: `A organização já usou ${Math.floor(proporcao * 100 + 1e-9)}% do armazenamento (${uso}).`,
  };
}

/**
 * A recusa pelo limite de usuários (#768), ou nula quando a falha é outra. A
 * API a manda com a extensão `limite`, no `CONFIRMACAO_EXIGIDA` (dá para
 * contratar) e no `CONFLITO_DE_ESTADO` (não dá).
 */
export function recusaDoLimite(falha: unknown): RecusaDoLimite | null {
  if (!(falha instanceof ErroDaApi)) return null;
  const limite = falha.problema.limite;
  if (!limite) return null;
  return {
    contratados: limite.contratados,
    emUso: limite.em_uso,
    valorDoAdicional: limite.usuario_adicional,
    podeContratar: limite.pode_contratar,
    valorMudou: limite.motivo === 'valor_mudou',
    recado: falha.problema.detail ?? null,
  };
}

/** O valor que a pessoa aceitou, como o convite e a reativação o mandam. */
export function valorAceito(valor: Dinheiro): { quantia_micros: number; moeda: 'BRL' } {
  return { quantia_micros: valor.quantia_micros, moeda: 'BRL' };
}
